/**
 * Store Table Model - Uses direct values from API response
 * Calculates discrepancies if reconciliation_variance is not provided
 */

interface ServerData {
  // New API response structure - nested store object
  store?: {
    id?: string;
    name?: string;
    logo_image_url?: string;
    logo_url?: string;
    subscription_status?: string;
    remaining_days?: number;
    is_credit_card?: boolean;
    user_role?: string;
    address_line1?: string;
    city?: string;
    state?: string;
    timezone?: string | null;
    pos_type?: string;
    lotto_authorized?: boolean;
  };

  // Legacy flat structure (for backward compatibility)
  store_id?: string;
  store_name?: string;
  logo_url?: string;
  subscription_status?: string;
  remaining_days?: number;
  is_credit_card?: boolean;
  user_role?: string;
  address?: string;
  lotto_authorized?: boolean;
  pos_type?: string;

  audit_summary?: {
    audit_count?: number;
    audit_window_start?: string | null;
    audit_window_end?: string | null;
    total_sold?: number;
    revenue?: number | string;
    total_revenue?: number | string;
    missing_in_date_range?: boolean;
  };

  pos_summary?: {
    scratcher_count?: number;
    scratcher_quantity_sold?: number; // Alternative field name
    scratcher_sales?: number | string;
    lotto_sales?: number | string;
    payout?: number | string;
    scratcher_free_tickets_paid_amount?: number;
    missing_in_date_range?: boolean;
  };

  lotto_summary?: {
    sales?: number | string;
    payouts_total?: number | string;
    scratcher_free_tickets_paid_amount?: number | string;
    missing_in_date_range?: boolean;
  };

  reconciliation_variance?: {
    scratcher?: {
      quantity_variance?: number;
      revenue_variance?: number;
    };
    lotto_sales_variance?: number;
    payout_variance?: number;
    total_variance?: number;
  };
}

interface Calculation {
  name: string;
  value: number;
  operation: 'add' | 'subtract';
}

class StoreTableModel {
  private _serverData: ServerData | null;

  constructor(serverData: ServerData | null = null) {
    this._serverData = serverData;
  }

  // Helper method to safely parse string/number values
  private parseValue(value: string | number | undefined | null): number {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  // Helper to get store field (checks both nested and flat structure)
  private getStoreField(field: 'id' | 'name' | 'logo_image_url' | 'logo_url' | 'subscription_status' | 'remaining_days' | 'is_credit_card' | 'pos_type' | 'lotto_authorized' | 'address_line1' | 'city' | 'state' | 'timezone'): any {
    if (this._serverData?.store) {
      return this._serverData.store[field];
    }
    // Fallback to flat structure
    if (field === 'id') return this._serverData?.store_id;
    if (field === 'name') return this._serverData?.store_name;
    if (field === 'logo_image_url' || field === 'logo_url') return this._serverData?.logo_url;
    if (field === 'subscription_status') return this._serverData?.subscription_status;
    if (field === 'remaining_days') return this._serverData?.remaining_days;
    if (field === 'is_credit_card') return this._serverData?.is_credit_card;
    if (field === 'pos_type') return this._serverData?.pos_type;
    if (field === 'lotto_authorized') return this._serverData?.lotto_authorized;
    return undefined;
  }

  // ===== BASIC STORE INFORMATION =====
  get id() { 
    return this.getStoreField('id') || '';
  }
  
  get name() { 
    return this.getStoreField('name') || '';
  }
  
  get logoImageUrl() { 
    return this.getStoreField('logo_image_url') || this.getStoreField('logo_url');
  }
  
  get subscriptionStatus() {
    return this.getStoreField('subscription_status') || 'active';
  }

  get remainingDays() {
    return this.getStoreField('remaining_days');
  }

  get isCreditCard() {
    return this.getStoreField('is_credit_card');
  }

  get address() {
    const addressLine1 = this.getStoreField('address_line1');
    const city = this.getStoreField('city');
    const state = this.getStoreField('state');
    
    if (addressLine1 || city || state) {
      return [addressLine1, city, state].filter(Boolean).join(', ') || 'Address not available';
    }
    
    return this._serverData?.address || 'Address not available';
  }

  get posType() {
    return this.getStoreField('pos_type') || this._serverData?.pos_type || 'n/a';
  }

  get lottoAuthorized() {
    const value = this.getStoreField('lotto_authorized');
    return value !== undefined ? value : (this._serverData?.lotto_authorized ?? false);
  }

  get timezone() {
    return this.getStoreField('timezone') || null;
  }

  // ===== AUDIT SUMMARY DATA =====
  get auditCount() {
    return this.parseValue(this._serverData?.audit_summary?.audit_count);
  }

  get auditWindowStart() {
    return this._serverData?.audit_summary?.audit_window_start || null;
  }

  get auditWindowEnd() {
    return this._serverData?.audit_summary?.audit_window_end || null;
  }

  get isAuditDataMissing() {
    return Boolean(this._serverData?.audit_summary?.missing_in_date_range);
  }

  get isPosDataMissing() {
    return Boolean(this._serverData?.pos_summary?.missing_in_date_range);
  }

  get isLottoDataMissing() {
    return Boolean(this._serverData?.lotto_summary?.missing_in_date_range);
  }

  get totalSold() {
    return this.parseValue(this._serverData?.audit_summary?.total_sold);
  }

  get revenue() {
    const revenue = this._serverData?.audit_summary?.revenue || this._serverData?.audit_summary?.total_revenue;
    return this.parseValue(revenue);
  }

  // ===== POS SUMMARY DATA =====
  get posScratcherCount() {
    const count = this._serverData?.pos_summary?.scratcher_count || 
                  this._serverData?.pos_summary?.scratcher_quantity_sold;
    return this.parseValue(count);
  }

  get posScratcherSales() {
    return this.parseValue(this._serverData?.pos_summary?.scratcher_sales);
  }

  get posLottoSales() {
    return this.parseValue(this._serverData?.pos_summary?.lotto_sales);
  }

  get posPayout() {
    return this.parseValue(this._serverData?.pos_summary?.payout);
  }

  // ===== LOTTO SUMMARY DATA =====
  get lottoSales() {
    return this.parseValue(this._serverData?.lotto_summary?.sales);
  }

  get lottoPayoutsTotal() {
    return this.parseValue(this._serverData?.lotto_summary?.payouts_total);
  }

  get scratcherFreeTicketsPaidAmount() {
    return this.parseValue(this._serverData?.lotto_summary?.scratcher_free_tickets_paid_amount);
  }

  // ===== RECONCILIATION VARIANCE (Direct from API or Calculated) =====
  get quantityVariance(): number | undefined {
    // Use direct variance if available
    if (this._serverData?.reconciliation_variance?.scratcher?.quantity_variance !== undefined) {
      return this._serverData.reconciliation_variance.scratcher.quantity_variance;
    }
    // Calculate if not provided
    const variance = this.posScratcherCount - this.totalSold;
    return variance !== 0 ? variance : undefined;
  }

  get revenueVariance(): number | undefined {
    // Use direct variance if available
    if (this._serverData?.reconciliation_variance?.scratcher?.revenue_variance !== undefined) {
      return this._serverData.reconciliation_variance.scratcher.revenue_variance;
    }
    // Calculate if not provided
    const variance = this.posScratcherSales - this.revenue;
    return variance !== 0 ? variance : undefined;
  }

  get lottoSalesVariance(): number | undefined {
    // Use direct variance if available
    if (this._serverData?.reconciliation_variance?.lotto_sales_variance !== undefined) {
      return this._serverData.reconciliation_variance.lotto_sales_variance;
    }
    // Calculate if not provided
    const variance = this.posLottoSales - this.lottoSales;
    return variance !== 0 ? variance : undefined;
  }

  get payoutVariance(): number | undefined {
    // Use direct variance if available
    if (this._serverData?.reconciliation_variance?.payout_variance !== undefined) {
      return this._serverData.reconciliation_variance.payout_variance;
    }
    // Calculate if not provided: (Lotto Payouts + Free Tickets) - POS Payout
    const variance = (this.lottoPayoutsTotal + this.scratcherFreeTicketsPaidAmount) - this.posPayout;
    return variance !== 0 ? variance : undefined;
  }

  get totalVariance(): number | undefined {
    // Use direct variance if available
    if (this._serverData?.reconciliation_variance?.total_variance !== undefined) {
      return this._serverData.reconciliation_variance.total_variance;
    }
    // Calculate if not provided
    const revenueVar = this.revenueVariance || 0;
    const lottoVar = this.lottoSalesVariance || 0;
    const payoutVar = this.payoutVariance || 0;
    const total = revenueVar + lottoVar + payoutVar;
    return total !== 0 ? total : undefined;
  }

  // ===== DISCREPANCY DETAILS METHODS (Using Direct Variance Values or Calculated) =====

  /**
   * Returns calculation details for scratcher counts discrepancy display
   * Shows popover for connected cases, including zero discrepancy
   */
  getScratcherCountsDiscrepancyDetails(): { calculations: Calculation[]; discrepancy: number } | null {
    const discrepancy = this.quantityVariance ?? (this.posScratcherCount - this.totalSold);

    return {
      calculations: [
        { name: 'POS Scratcher Quantity Sold', value: this.posScratcherCount, operation: 'add' as const },
        { name: 'Audit Total Sold', value: this.totalSold, operation: 'subtract' as const }
      ],
      discrepancy
    };
  }

  /**
   * Returns calculation details for scratcher sales discrepancy display
   * Shows popover for connected cases, including zero discrepancy
   */
  getScratcherSalesDiscrepancyDetails(): { calculations: Calculation[]; discrepancy: number } | null {
    const discrepancy = this.revenueVariance ?? (this.posScratcherSales - this.revenue);

    return {
      calculations: [
        { name: 'POS Scratcher Sales', value: this.posScratcherSales, operation: 'add' as const },
        { name: 'Audit Revenue', value: this.revenue, operation: 'subtract' as const }
      ],
      discrepancy
    };
  }

  /**
   * Returns calculation details for lotto sales discrepancy display
   * Shows popover for connected cases, including zero discrepancy
   */
  getLottoSalesDiscrepancyDetails(): { calculations: Calculation[]; discrepancy: number } | null {
    const discrepancy = this.lottoSalesVariance ?? (this.posLottoSales - this.lottoSales);

    return {
      calculations: [
        { name: 'POS Lotto Sales', value: this.posLottoSales, operation: 'add' as const },
        { name: 'Lottery System Reported', value: this.lottoSales, operation: 'subtract' as const }
      ],
      discrepancy
    };
  }

  /**
   * Returns calculation details for payout discrepancy display
   * Shows popover for connected cases, including zero discrepancy
   */
  getPayoutDiscrepancyDetails(): { calculations: Calculation[]; discrepancy: number } | null {
    const discrepancy = this.payoutVariance ?? ((this.lottoPayoutsTotal + this.scratcherFreeTicketsPaidAmount) - this.posPayout);

    return {
      calculations: [
        { name: 'Lotto summary Payouts', value: this.lottoPayoutsTotal, operation: 'add' as const },
        { name: 'Free Tickets Paid', value: this.scratcherFreeTicketsPaidAmount, operation: 'add' as const },
        { name: 'POS Payouts', value: this.posPayout, operation: 'subtract' as const }
      ],
      discrepancy
    };
  }

  /**
   * Returns the total variance directly from API response or calculated
   * No calculation needed if provided by API
   */
  getAllDiscrepanciesTotal(): number {
    return this.totalVariance ??
      ((this.revenueVariance ?? 0) + (this.lottoSalesVariance ?? 0) + (this.payoutVariance ?? 0));
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: ServerData): StoreTableModel {
    return new StoreTableModel(serverData);
  }
}

export default StoreTableModel;
